import React, { useEffect, useState } from "react";
import { apiRequest } from "../../../../service/apiRequest";
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
  Dropdown,
} from "antd";
import { createStyles } from "antd-style";
import { HomeOutlined, DeleteOutlined, EditOutlined, CheckCircleFilled, ExclamationCircleFilled, DownOutlined } from "@ant-design/icons";
import { theme } from "antd";

import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
// import WoodModal from './WoodModal';
import { showSuccess, showError } from "../../../../components/errorHandler";
import type { PopconfirmProps, TableColumnsType } from "antd";
import { useCurrentModule } from "../../../../hooks/useCurrentModule";

import { HPO } from "../../../../types/HPO";

import AddPO from "./add-po";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

const PurchaseHandicraftOrder: React.FC = () => {
  const { ledgers, woods, woodtypes, loaded } = useAppSelector(
    (state) => state.master,
  );
  const [hpurchaseorders, setHPO] = useState<HPO[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<HPO | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [woodtype, setWoodType] = useState<any>({});

  const cancel: PopconfirmProps["onCancel"] = (e) => {
    console.log(e);
    showError("Click on No");
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchHPO = async (page = 1, limit = 10) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        wood_type: woodtype,
        bill_type: "timber",
      });

      const data = await apiRequest<HPO[]>({
        method: "get",
        url: `get-hpo?${params.toString()}`,
      });
      setHPO(data);
    } catch (error) {
      showError("Failed to fetch HPO");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchHPO();
  }, []);

  const handleEditClick = async (record: HPO) => {
    try {
      const response = await apiRequest<any>({
        method: "get",
        url: `get-po-bill?id=${record.pbill_id}`,
      });

      if (!response) {
        showError("Failed to load bill details");
        return;
      }

      setIsEditMode(true);
      setInitialValues({
        pbill_id: response.pbill_id,
        ledger_id: response.pbill_ledger_id,
        bill_no: response.pbill_bill_no,
        invoice_no: response.pbill_invoice_no,
        challan_no: response.pbill_challan_no,
        pbill_po_no: response.pbill_po_no,
        bill_date: response.pbill_bill_date,
        motor_vehicle_no: response.pbill_motor_vehicle_no,
        freight_outward: response.pbill_fright_amount,
        po_details: response.po_details || [],
        ledger_company_name: record.ledger_company_name,
        ledger_gstin: record.ledger_gstin,
        has_napi: record.has_napi,
        self_napi_exists: record.self_napi_exists,
        party_napi_exists: record.party_napi_exists,
      } as any);
      setIsModalVisible(true);
    } catch (error) {
      console.error(error);
      showError("Error loading bill details");
    }
  };

  const handleCreate = () => {
    fetchHPO();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pbill_id: number) => {
    try {
      await apiRequest({
        method: "delete",
        url: `/delete-hpo/${pbill_id}`,
      });
      showSuccess("HPO deleted");
      fetchHPO();
    } catch {
      showError("Failed to delete HPO");
    }
  };

  const handleStatusToggle = async (widthId: number) => {
    setHPO((prev) =>
      prev.map((item) =>
        item.pbill_id === widthId
          ? { ...item, pbill_status: item.pbill_status === 1 ? 0 : 1 }
          : item,
      ),
    );

    try {
      await apiRequest({
        method: "put",
        url: `/update-status-hpo/${widthId}`,
      });
      showSuccess("HPO status updated");
    } catch (error) {
      showError("Failed to update status");
      setHPO((prev) =>
        prev.map((item) =>
          item.pbill_id === widthId
            ? { ...item, pbill_status: item.pbill_status === 1 ? 0 : 1 }
            : item,
        ),
      );
    }
  };

  const handleTableChange: TableProps<HPO>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<HPO> = [
    {
      title: "Sr.",
      key: "sr",
      width: 60,
      render: (_: any, __: HPO, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Bill Details",
      dataIndex: "pbill_id",
      key: "pbill_id",
      render: (_, record) => (
        <div>
          <div>
            <strong>Bill No.:</strong> {record.pbill_bill_no}
            <br />
            <strong>PO No.:</strong> {record.pbill_po_no || "—"}
            <br />
            <strong>Challan No.:</strong> {record.pbill_challan_no}
            <br />
            <strong>Bill Date:</strong> {record.pbill_bill_date}
            <br />
          </div>

          <div style={{ marginTop: 8 }}>
            {record.self_napi_exists && record.party_napi_exists ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#52c41a', fontWeight: '600', fontSize: '13px' }}>
                <CheckCircleFilled style={{ color: '#52c41a', fontSize: '15px' }} />
                Both NAPI Present
              </span>
            ) : record.party_napi_exists ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#faad14', fontWeight: '600', fontSize: '13px' }}>
                <ExclamationCircleFilled style={{ color: '#faad14', fontSize: '15px' }} />
                Only Party NAPI
              </span>
            ) : record.self_napi_exists ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#faad14', fontWeight: '600', fontSize: '13px' }}>
                <ExclamationCircleFilled style={{ color: '#faad14', fontSize: '15px' }} />
                Only Self NAPI
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#ff4d4f', fontWeight: '600', fontSize: '13px' }}>
                <ExclamationCircleFilled style={{ color: '#ff4d4f', fontSize: '15px' }} />
                NAPI Missing
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "ledger_company_name",
      key: "ledger_company_name",
      render: (_, record) => (
        <div>
          {record.ledger_company_name} (<strong>{record.ledger_gstin}</strong>)
        </div>
      ),
    },
  ];

  // Conditionally add action column
  if (
    currentModule?.right_edit === "Y" ||
    currentModule?.right_delete === "Y"
  ) {
    columns.push({
      title: "Action",
      width: 320,
      fixed: "right" as const,
      render: (_: any, record: HPO) => (
        <Space size="middle">
          {currentModule?.right_edit === "Y" && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pbill_status}
                onChange={() => handleStatusToggle(record.pbill_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === "Y" && (
            <Popconfirm
              title="Delete this HPO?"
              onConfirm={() => handleDelete(record.pbill_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
          <Dropdown
            menu={{
              items: [
                {
                  key: 'party',
                  label: record.party_napi_exists ? "Edit Party NAPI" : "Create Party NAPI",
                  onClick: () => {
                    window.location.href = `/party-napi/${record.pbill_id}`;
                  },
                },
                {
                  key: 'self',
                  label: record.self_napi_exists ? "Edit Self NAPI" : "Create Self NAPI",
                  onClick: () => {
                    window.location.href = `/self-napi/${record.pbill_id}`;
                  },
                },
                {
                  key: 'cutting',
                  label: "Cutting Napi",
                  onClick: () => {
                    window.location.href = `/cutting-napi/${record.pbill_id}`;
                  },
                },
                {
                  key: 'return',
                  label: "Return Napi",
                  onClick: () => {
                    window.location.href = `/return-napi/${record.pbill_id}`;
                  },
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="primary" className="px-6">
              NAPI <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: "flex",
          marginBottom: 16,
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Breadcrumb style={{ display: "inline-block", margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Purchase</Breadcrumb.Item>
        </Breadcrumb>
        <Flex gap="small">
          <Flex
            gap="middle"
            align="center"
            style={{ marginBottom: 0, display: "flex" }}
            wrap
          >
            <Select
              showSearch
              optionFilterProp="label"
              onChange={(value) => setWoodType(value)}
              placeholder="Select wood type"
              options={woodtypes.map((wt) => ({
                label: wt.wood_name,
                value: wt.wood_id,
              }))}
            />
          </Flex>
          <Flex gap="small">
            <Button
              style={{ display: "flex", alignSelf: "center" }}
              type="primary"
              onClick={() => fetchHPO(1, pagination.pageSize)}
            >
              Search
            </Button>
          </Flex>
          {currentModule?.right_add === "Y" && (
            <Flex gap="small" align="center">
              <Button type="primary" onClick={showCreateModal}>
                Add Purchase
              </Button>
            </Flex>
          )}
        </Flex>
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<HPO>
          tableLayout="fixed"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pbill_id"
          dataSource={hpurchaseorders.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize,
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 17, x: "max-content" }}
          locale={{ emptyText: "No Purchase order found" }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(
                  pagination.current * pagination.pageSize,
                  hpurchaseorders.length,
                )}{" "}
                of {hpurchaseorders.length}
              </span>
              <Pagination
                {...pagination}
                total={hpurchaseorders.length}
                showSizeChanger
                onChange={(page, size) => fetchHPO(page, size)}
              />
            </Flex>
          )}
        />
      </div>

      <AddPO
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        woodTypesData={woodtypes}
        ledgerData={ledgers}
        woodData={woods}
      />
    </>
  );
};

export default PurchaseHandicraftOrder;
